import React, { useEffect, useState } from "react";
import financeApiService from "../../../../services/financeApiService";
import financeV2Adapters from "../../../../adapters/financeV2Adapters";
import styles from "./EnveloppeDashboard.module.scss";
import CircularGauge from "./CircularGauge";
import EnveloppeCard from "../../Financiers/components/EnveloppeCard";

const fmtEur = (n) => `${Number(n || 0).toLocaleString("fr-FR")} €`;

// Vue détaillée : un groupe par financeur avec les grandes cartes par enveloppe.
const DetailedGroups = ({ groupes }) => (
  <>
    {groupes.map((g) => {
      const actives = (g.enveloppes || []).filter((e) => e.estActive);
      if (actives.length === 0) return null;
      const cumulDispo = g.consommation?.disponible ?? 0;
      return (
        <div key={g.financeurUuid} className={styles.group}>
          <div className={styles.groupHead}>
            <span className={styles.groupName}>{g.financeurNom}</span>
            <span className={styles.groupCumul}>
              Disponible cumulé&nbsp;: {fmtEur(cumulDispo)}
            </span>
          </div>
          <div className={styles.dashboard}>
            {actives.map((env, index) => (
              <EnveloppeCard
                key={env.uuid}
                consommation={{
                  financeur: { nom: env.nom || "Enveloppe" },
                  enveloppeOrigine:
                    env.consommation.enveloppeOrigine || env.montant,
                  reserve: env.consommation.reserve,
                  engage: env.consommation.engage,
                  disponible: env.consommation.disponible,
                }}
                index={index}
              />
            ))}
          </div>
        </div>
      );
    })}
  </>
);

// Carte condensée d'un financeur : une jauge circulaire par enveloppe active.
const FinanceurCondensedCard = ({ groupe, onOpen }) => {
  const actives = (groupe.enveloppes || []).filter((e) => e.estActive);
  if (actives.length === 0) return null;
  const c = groupe.consommation;
  const consoRatio = financeV2Adapters.enveloppeRatio(
    c.reserve + c.engage,
    c.enveloppeOrigine,
  );
  const colorText = financeV2Adapters.enveloppeColorText(consoRatio);

  return (
    <button
      type="button"
      className={styles.condCard}
      onClick={() => onOpen(groupe.financeurUuid)}
      title={`Voir le détail de ${groupe.financeurNom}`}
    >
      <div className={styles.condHead}>
        <span className={styles.condName}>{groupe.financeurNom}</span>
        <span className={styles.condDispo} style={{ color: colorText }}>
          {fmtEur(c.disponible)}
        </span>
      </div>
      <div className={styles.condGauges}>
        {actives.map((env) => {
          const ec = env.consommation;
          const origine = ec.enveloppeOrigine || env.montant || 0;
          const dispo = ec.disponible;
          const cRatio = financeV2Adapters.enveloppeRatio(
            ec.reserve + ec.engage,
            origine,
          );
          const dispoRatio = financeV2Adapters.enveloppeRatio(dispo, origine);
          return (
            <CircularGauge
              key={env.uuid}
              value={dispoRatio}
              color={financeV2Adapters.enveloppeColor(cRatio)}
              label={env.nom || "Enveloppe"}
              sublabel={fmtEur(dispo)}
              ariaLabel={`${env.nom || "Enveloppe"} : ${Math.round(
                dispoRatio,
              )} % disponible, ${fmtEur(dispo)} restants`}
            />
          );
        })}
      </div>
      <div className={styles.condFoot}>
        {actives.length} enveloppe{actives.length > 1 ? "s" : ""} · voir le détail
      </div>
    </button>
  );
};

const EnveloppeDashboard = ({ financiers }) => {
  // Un groupe par financeur : { financeurUuid, financeurNom, consommation
  // (cumul), enveloppes (chacune avec sa propre consommation) }.
  const [groupes, setGroupes] = useState([]);
  const [view, setView] = useState("condensed");
  const [focusUuid, setFocusUuid] = useState(null);

  // Clé stable pour éviter une boucle de re-fetch (identités d'objets
  // potentiellement neuves à chaque rendu du parent).
  const financeursKey = (financiers || []).map((f) => f.uuid).join(",");

  useEffect(() => {
    if (!financiers || financiers.length === 0) {
      setGroupes([]);
      return undefined;
    }
    let cancelled = false;
    Promise.all(
      financiers.map((f) =>
        financeApiService
          .listerEnveloppes(f.uuid)
          .then((res) => ({
            f,
            data: financeV2Adapters.fromApi.enveloppesList(res.data),
          }))
          .catch(() => null),
      ),
    ).then((results) => {
      if (cancelled) return;
      setGroupes(
        results.filter(Boolean).map(({ f, data }) => ({
          financeurUuid: f.uuid,
          financeurNom: f.nom || f.name || "—",
          consommation: data.consommation,
          enveloppes: data.enveloppes,
        })),
      );
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financeursKey]);

  if (!financiers || financiers.length === 0) {
    return <div className={styles.empty}>Aucun financeur disponible.</div>;
  }

  // Groupes ayant au moins une enveloppe active.
  const groupesActifs = groupes.filter((g) =>
    (g.enveloppes || []).some((e) => e.estActive),
  );

  // Totaux globaux — toutes enveloppes actives confondues (somme des cumuls).
  const totaux = groupesActifs.reduce(
    (acc, g) => {
      const c = g.consommation;
      acc.origine += c.enveloppeOrigine;
      acc.dispo += c.disponible;
      acc.reserve += c.reserve;
      acc.engage += c.engage;
      acc.consomme += c.reserve + c.engage;
      return acc;
    },
    { origine: 0, dispo: 0, reserve: 0, engage: 0, consomme: 0 },
  );
  const globalConsoRatio = financeV2Adapters.enveloppeRatio(
    totaux.consomme,
    totaux.origine,
  );
  const globalDispoRatio = financeV2Adapters.enveloppeRatio(
    totaux.dispo,
    totaux.origine,
  );
  const globalColor = financeV2Adapters.enveloppeColor(globalConsoRatio);
  const globalColorText =
    financeV2Adapters.enveloppeColorText(globalConsoRatio);

  const openDetail = (finUuid) => {
    setFocusUuid(finUuid);
    setView("detailed");
  };
  const detailGroupes = focusUuid
    ? groupesActifs.filter((g) => g.financeurUuid === focusUuid)
    : groupesActifs;

  return (
    <div className={styles.dashboardRoot}>
      {/* ── Résumé global ── */}
      <div className={styles.summary}>
        <div className={styles.summaryMain}>
          <div className={styles.summaryLabel}>
            Disponible — toutes enveloppes
          </div>
          <div
            className={styles.summaryValue}
            style={{ color: globalColorText }}
          >
            {fmtEur(totaux.dispo)}
          </div>
          <div className={styles.summaryBar}>
            <div
              className={styles.summaryBarFill}
              style={{
                width: `${globalDispoRatio}%`,
                background: globalColor,
              }}
            />
          </div>
          <div className={styles.summaryMeta}>
            sur {fmtEur(totaux.origine)} alloués ·{" "}
            {Math.round(globalConsoRatio)} % consommé (réservé + engagé)
          </div>
        </div>
        <div className={styles.summaryChips}>
          <div className={styles.summaryChip}>
            <span className={styles.chipLabel}>Réservé</span>
            <span className={styles.chipValue}>{fmtEur(totaux.reserve)}</span>
          </div>
          <div className={styles.summaryChip}>
            <span className={styles.chipLabel}>Engagé</span>
            <span className={styles.chipValue}>{fmtEur(totaux.engage)}</span>
          </div>
        </div>
      </div>

      {/* ── Barre d'outils : titre / retour + bascule de vue ── */}
      <div className={styles.toolbar}>
        <span className={styles.toolbarTitle}>
          {view === "detailed" && focusUuid ? (
            <button
              type="button"
              className={styles.backLink}
              onClick={() => setFocusUuid(null)}
            >
              <i className="fa fa-arrow-left" /> Tous les financeurs
            </button>
          ) : (
            "Par financeur"
          )}
        </span>
        <div
          className={styles.viewToggle}
          role="group"
          aria-label="Affichage du dashboard"
        >
          <button
            type="button"
            className={view === "condensed" ? styles.viewToggleActive : ""}
            aria-pressed={view === "condensed"}
            onClick={() => {
              setView("condensed");
              setFocusUuid(null);
            }}
          >
            <i className="fa fa-th-large" /> Condensé
          </button>
          <button
            type="button"
            className={view === "detailed" ? styles.viewToggleActive : ""}
            aria-pressed={view === "detailed"}
            onClick={() => setView("detailed")}
          >
            <i className="fa fa-list" /> Détaillé
          </button>
        </div>
      </div>

      {view === "condensed" ? (
        <div className={styles.condGrid}>
          {groupesActifs.map((g) => (
            <FinanceurCondensedCard
              key={g.financeurUuid}
              groupe={g}
              onOpen={openDetail}
            />
          ))}
        </div>
      ) : (
        <DetailedGroups groupes={detailGroupes} />
      )}
    </div>
  );
};

export default EnveloppeDashboard;
